import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Api } from 'src/app/services/api';
import { Globals } from 'src/app/services/globals';
import { PosterGenerationState } from 'src/app/services/poster-generation-state';

import '@interactjs/auto-start'
import '@interactjs/actions/drag'
import '@interactjs/actions/resize'
import '@interactjs/modifiers'
import '@interactjs/dev-tools'
import Interact from '@interactjs/interact'
import { DomSanitizer } from '@angular/platform-browser';
import { PosterSize, posterSizesByName } from '../../../../../../../common/common-types/poster-size';
import { WooCommerce } from 'src/app/services/woocommerce';
import { Scroll } from 'src/app/services/scroll';

@Component({
    selector: 'poster-alignment-configurator',
    templateUrl: './poster-alignment-configurator.component.html',
    styleUrls: ['./poster-alignment-configurator.component.scss'],
    standalone: false
})
export class PosterAlignmentConfiguratorComponent implements AfterViewInit, OnDestroy {
  @Input()
  size: PosterSize;

  @Input()
  imageUrl: string;

  @Input()
  posterAlignment: { xOffset: number, yOffset: number } = { xOffset: 0, yOffset: 0 };

  availableSizes = posterSizesByName;
  safeUrl: any;

  @ViewChild('dragAreaElement') dragAreaElement?: ElementRef<HTMLDivElement>;
  @ViewChild('sizeBorderElement') sizeBorderElement?: ElementRef<HTMLDivElement>;
  @ViewChild('posterImageElement') posterImageElement?: ElementRef<HTMLImageElement>;

  imageOrientation: 'portrait' | 'landscape';
  private interactable?: any;

  constructor(public globals: Globals, private elementRef: ElementRef, private api: Api, private sanitizer: DomSanitizer, private wooCommerce: WooCommerce, private scroll: Scroll
  ) {

  }

  async ngOnInit() {
    this.safeUrl = this.sanitizer.bypassSecurityTrustUrl(this.imageUrl);
    const [width, height] = this.size.split('x').map(value => parseInt(value));
    this.imageOrientation = width > height ? 'landscape' : 'portrait';
  }

  saveAlignment() {

  }


  async ngAfterViewInit() {
    await this.waitForImage();
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

    const image = this.posterImageElement!.nativeElement;
    const border = this.sizeBorderElement!.nativeElement.getBoundingClientRect();
    const dragArea = this.dragAreaElement!.nativeElement.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    const translateX = border.left - dragArea.left - ((imageRect.width - border.width) / 2);

    this.setImageTranslation(this.clampTranslation(translateX));
    this.updateAlignment();

    this.interactable = (Interact as any)(image).draggable({
      inertia: true,
      startAxis: 'x',
      lockAxis: 'x',
      autoScroll: true,
      listeners: {
        move: (event: any) => {
          const currentX = parseFloat(image.getAttribute('data-x') || '0');
          this.setImageTranslation(this.clampTranslation(currentX + event.dx));
        },
        end: () => {
          this.setImageTranslation(this.clampTranslation(
            parseFloat(image.getAttribute('data-x') || '0')
          ));
          this.updateAlignment();
        }
      }
    });
  }

  ngOnDestroy() {
    this.interactable?.unset();
  }

  private async waitForImage(): Promise<void> {
    const image = this.posterImageElement!.nativeElement;
    if (image.complete && image.naturalWidth > 0)
      return;

    await new Promise<void>((resolve, reject) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => reject(new Error('Could not load poster image')), { once: true });
    });
  }

  private clampTranslation(proposedX: number): number {
    const image = this.posterImageElement!.nativeElement;
    const imageRect = image.getBoundingClientRect();
    const borderRect = this.sizeBorderElement!.nativeElement.getBoundingClientRect();
    const currentX = parseFloat(image.getAttribute('data-x') || '0');
    const untranslatedLeft = imageRect.left - currentX;
    const minLeft = borderRect.right - imageRect.width;
    const maxLeft = borderRect.left;

    if (minLeft > maxLeft)
      return currentX + (borderRect.left + ((borderRect.width - imageRect.width) / 2) - imageRect.left);

    const proposedLeft = untranslatedLeft + proposedX;
    const clampedLeft = Math.min(maxLeft, Math.max(minLeft, proposedLeft));
    return clampedLeft - untranslatedLeft;
  }

  private setImageTranslation(x: number) {
    const image = this.posterImageElement!.nativeElement;
    image.style.transform = `translate(${x}px, 0px)`;
    image.setAttribute('data-x', `${x}`);
    image.setAttribute('data-y', '0');
  }

  private updateAlignment() {
    const imageRect = this.posterImageElement!.nativeElement.getBoundingClientRect();
    const borderRect = this.sizeBorderElement!.nativeElement.getBoundingClientRect();
    this.posterAlignment.xOffset = (imageRect.left - borderRect.left) / borderRect.width;
    this.posterAlignment.yOffset = 0;
  }

}
